import { parseDate } from "../util/utils";
import UserAuth from "./../models/UserAuth";
import { apiRequest } from "./APIService";
import examsService from "./ExamsService";

const USER_TYPES = ["L2L", "HOSP", "PSC"];

class AuthenticationService {
  constructor() {
    this.userAuth = null;
    
  }

  async login(userId, password, userType) {
    try {
      if (userId != null && password != null && userType != null) {
        if (USER_TYPES.indexOf(userType) <= -1) {
          console.error(
            `Wrong user type: ${userType}, valid types: ${USER_TYPES}`
          );

          return {
            status: false,
          };
        } else {
          let userLoginRequest = {
            userId: userId,
            password: password,
            userType: userType,
          };

          let userAuth = await apiRequest("POST", "/Autenticar", userLoginRequest);

          if (userAuth) {
            this.userAuth = userAuth;
          }

          if (
            userAuth &&
            userAuth.relatedClients &&
            userAuth.relatedClients.length &&
            userAuth.relatedClients.length == 1
          ) {
            let clientAuth = await this.requestClientToken(
              userAuth.relatedClients[0].code
            );

            if (clientAuth) {
              userAuth = clientAuth;
            } else {
              return {
                status: false,
              };
            }
          }

          if (
            userAuth &&
            userAuth.relatedClients &&
            userAuth.relatedClients.length &&
            userAuth.relatedClients.length > 1
          ) {
            return {
              status: true,
              relatedClients: userAuth.relatedClients,
            };
          }

          if (userAuth) {
            await this.setAuthorizationDataOnLocalStorage(userAuth);
            this.userAuth = null;
            return {
              status: true,
              relatedClients: null,
            };
          }

          return {
            status: false,
          };
        }
      } else {
        console.error(
          "Missing or invalid required login parameter: user, password or type"
        );
        return {
          status: false,
        };
      }
    } catch (error) {
      console.error("Authentication error:", error.message);
      return {
        status: false,
      };
    }
  }

  async requestClientToken(clientId) {
    try {
      let userAuth = this.userAuth || this.retrieveUserDataFromLocalStorage();

      if (userAuth) {
        userAuth.clientId = clientId;
        userAuth.renew = 1;
        userAuth.contextRoot = "";
        userAuth.siglaUsuario = "";
      }

      let clientAuth = await apiRequest("POST", "/SelecionarCodigoLc", userAuth);

      if (clientAuth) {
        userAuth.token = clientAuth.token;
        userAuth.tokenExpirationDate = clientAuth.tokenExpirationDate;
        userAuth.tokenExpirationInMinutes = clientAuth.tokenExpirationInMinutes;
        userAuth.clientId = clientAuth.clientId;
        userAuth.clientName = clientAuth.clientName;
      } else {
        this.userAuth = null;
        return this.userAuth;
      }

      return userAuth;
    } catch (error) {
      console.error(
        `Error when trying to request client token: ${error.message}`
      );
      return null;
    }
  }

  async logout() {
    try {
      let userAuth = this.retrieveUserDataFromLocalStorage();
      
      if (userAuth && userAuth.token) {
        examsService.stopGettingAllData();
        let logOut = await apiRequest("POST", "/InvalidarToken", { token: userAuth.token });

        if(!logOut || logOut.status == 200 ){
          localStorage.removeItem("userAuth");
          return true;
        } else {
          localStorage.removeItem("userAuth");
        }
      }
      return null;
    } catch (error) {
      console.error(
        `Error when trying to log user out: ${error.message}`
      );
      return null;
    }
  }

  endSession(){
    localStorage.removeItem("userAuth");
  }

  registerSession(session){
    try {
      let userAuth = this.retrieveUserDataFromLocalStorage();

      if (userAuth) {
        userAuth[`${session}Session`] = true;
      }

      localStorage.setItem("userAuth", JSON.stringify(userAuth));

      return true;

    } catch (error) {
      console.error(
        `Error when trying to register my pardini call: ${error.message}`
      );
      return false;
    }
  }

  setUserAuthorizationDataOnRequest(request) {
    try {
      let userAuth = this.retrieveUserDataFromLocalStorage();

      if (!userAuth) {
        return null;
      }

      if (typeof request === "object") {
        request = { ...request, ...userAuth };

        return request;
      } else {
        return null;
      }
    } catch (error) {
      console.error(
        `Error when trying to save authorization data: ${error.message}`
      );
      return null;
    }
  }

  async setAuthorizationDataOnLocalStorage(authData) {
    new Promise((resolve, reject) => {
      if (authData) {
        let parsedDate = parseDate(authData.tokenExpirationDate);
        
        let userAuth = new UserAuth(
          authData.userId,
          authData.userType,
          authData.userName,
          authData.authkeyType,
          authData.token,
          parsedDate || Date.parse(authData.tokenExpirationDate)
        );

        if (authData.relatedClients) {
          userAuth.relatedClients = authData.relatedClients;
          userAuth.clientId = authData.clientId;
          userAuth.clientName = authData.clientName;
        }

        if (authData.myPardiniSession) {
          userAuth.myPardiniSession = authData.myPardiniSession;
        }
        
        localStorage.setItem("userAuth", JSON.stringify(userAuth));

        const verifyLocalStorage = () => {
          setTimeout(() => {
            if (localStorage.getItem("userAuth")) {
              return resolve();
            } else {
              verifyLocalStorage();
            }
          }, 100);
        };

        verifyLocalStorage();
      } else {
        resolve();
      }
    });
  }

  async setUserClient(clientId, clientName) {
    try {
      let userAuth = this.userAuth || this.retrieveUserDataFromLocalStorage();
      let myPardiniSession = userAuth && userAuth.myPardiniSession;

      if (userAuth && clientId != null && clientName != null) {
        let clientAuth = await this.requestClientToken(clientId);

        if (clientAuth) {
          userAuth = clientAuth;

          if(myPardiniSession){
            userAuth.myPardiniSession = myPardiniSession;
          }

          await this.setAuthorizationDataOnLocalStorage(userAuth);

          this.userAuth = null;

          return {
            status: true,
            relatedClients: null,
          };
        } else {
          return {
            status: false,
          };
        }
      } else {
        return {
          status: false,
        };
      }
    } catch (error) {
      console.error(
        `Error when trying to select user client: ${error.message}`
      );
      return {
        status: false,
      };
    }
  }

  checkIfUserAuthenticated() {
    try {
      let userAuth = localStorage.getItem("userAuth");

      userAuth = userAuth ? JSON.parse(userAuth) : false;

      if (
        !(
          userAuth &&
          userAuth.tokenExpirationDate &&
          !this.checkIfTokenExpired(userAuth.tokenExpirationDate)
        )
      ) {
        return null;
      }
      return true;
    } catch (error) {
      console.error(
        `Error when trying to check user authentication: ${error.message}`
      );
      return null;
    }
  }

  retrieveUserDataFromLocalStorage() {
    try {
      let userAuth = localStorage.getItem("userAuth");

      userAuth = userAuth ? JSON.parse(userAuth) : false;

      if (userAuth) {
        return userAuth;
      }

      return null;
    } catch (error) {
      console.error(
        `Error when trying to retrieve user data: ${error.message}`
      );
      return null;
    }
  }

  checkIfTokenExpired(expirationDate) {
    return !expirationDate || Date.now() >= expirationDate;
  }

  async checkIfTokenValidOnRemote(token) {
    try {
      if (token) {
        let userAuth = await apiRequest("POST", "/ConfirmarToken", { token });

        if (userAuth) {
          await this.setAuthorizationDataOnLocalStorage(userAuth);
          return true;
        }
      }

      return null;
    } catch (error) {
      console.error(
        `Error when trying to check token on remote: ${error.message}`
      );
      return null;
    }
  }
}

export default new AuthenticationService();